import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  ORDER_LIST_LIMIT,
  ORDERS_PAGE,
  type Order,
  type OrderStatus,
} from '../constants/orders'
import { QUERY_KEYS } from '../constants/query'
import { ApiError } from '../helpers/api-client'
import { toApiMessage } from '../helpers/api-error'
import { fetchOrder, fetchOrders } from '../helpers/orders-api'
import { upsertMatchingQueryItems } from '../helpers/query-list'

function toMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

export function useOrders() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<'all' | OrderStatus>('all')
  const ordersQuery = useQuery({
    queryKey: QUERY_KEYS.ordersList(statusFilter, ORDER_LIST_LIMIT),
    queryFn: () =>
      fetchOrders({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: ORDER_LIST_LIMIT,
      }),
  })
  const items = ordersQuery.data ?? []
  const loadError = ordersQuery.error
    ? toApiMessage(ordersQuery.error, ORDERS_PAGE.loadError)
    : ''
  const [selected, setSelected] = useState<Order | null>(null)
  const [error, setError] = useState('')
  const [openingId, setOpeningId] = useState<string | null>(null)

  async function openOrder(id: string) {
    const current = items.find((item) => item.id === id) ?? null
    setSelected(current)
    setError('')
    setOpeningId(id)
    try {
      const detail = await fetchOrder(id)
      upsertMatchingQueryItems(queryClient, QUERY_KEYS.orders, detail)
      setSelected(detail)
    } catch (err) {
      setError(toMessage(err, ORDERS_PAGE.loadError))
    } finally {
      setOpeningId(null)
    }
  }

  const visibleItems =
    statusFilter === 'all'
      ? items
      : items.filter((order) => order.status === statusFilter)

  return {
    items: visibleItems,
    loading: ordersQuery.isPending && !ordersQuery.data && !loadError,
    loadError,
    statusFilter,
    setStatusFilter,
    selected,
    setSelected,
    error,
    openingId,
    openOrder,
  }
}
